import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function KinematicsCalculator() {
  const [initialVelocity, setInitialVelocity] = useState("");
  const [acceleration, setAcceleration] = useState("");
  const [time, setTime] = useState("");
  const [result, setResult] = useState<{ velocity: number; displacement: number } | null>(null);

  const calculate = () => {
    const v0 = parseFloat(initialVelocity);
    const a = parseFloat(acceleration);
    const t = parseFloat(time);

    if (isNaN(v0) || isNaN(a) || isNaN(t)) {
      toast.error("Please enter valid values");
      return;
    }

    const finalVelocity = v0 + a * t;
    const displacement = v0 * t + 0.5 * a * t * t;
    
    setResult({ velocity: finalVelocity, displacement });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setInitialVelocity("");
    setAcceleration("");
    setTime("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Kinematics Calculator" 
      calculatorPath="/calculator/kinematics"
      category="physics"
      subcategory="Mechanics"
    >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Kinematics Calculator</h1>
            <p className="text-muted-foreground">
              Calculate velocity and displacement with constant acceleration
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formulas</CardTitle>
              <CardDescription>
                v = v₀ + at | s = v₀t + ½at²
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="initialVelocity">Initial Velocity (m/s)</Label>
                  <Input
                    id="initialVelocity"
                    type="number"
                    step="0.01"
                    placeholder="Enter initial velocity"
                    value={initialVelocity}
                    onChange={(e) => setInitialVelocity(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="acceleration">Acceleration (m/s²)</Label>
                  <Input
                    id="acceleration"
                    type="number"
                    step="0.01"
                    placeholder="Enter acceleration"
                    value={acceleration}
                    onChange={(e) => setAcceleration(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time">Time (s)</Label>
                  <Input
                    id="time"
                    type="number"
                    step="0.01"
                    placeholder="Enter time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculate} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <div className="space-y-3">
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Final Velocity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {result.velocity.toFixed(2)} m/s
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Displacement</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-accent">
                          {result.displacement.toFixed(2)} m
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <CalculatorSuperCalcCTA config={calculatorCTAs["kinematics"]} trigger={result.velocity} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the initial velocity in m/s</p>
                  <p>2. Enter the acceleration in m/s²</p>
                  <p>3. Enter the time in seconds</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 10 m/s initial velocity, 2 m/s² acceleration for 5 seconds
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
